import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  materialService,
  Material,
  MaterialFormData,
  MaterialListParams,
  MaterialsListParams,
  MaterialsByGstParams,
  BulkMaterialFormData
} from '@/services/material/materialService';

export interface MaterialState {
  materials: Material[];
  materialsList: Material[];
  materialsListLoading: boolean;
  material: Material | null;
  materialsLoading: boolean;
  isLoading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  } | null;
}

const initialState: MaterialState = {
  materials: [],
  materialsList: [],
  materialsListLoading: false,
  material: null,
  materialsLoading: false,
  isLoading: false,
  error: null,
  pagination: null,
};

export const fetchMaterials = createAsyncThunk(
  'material/fetchMaterials',
  async (params: MaterialListParams, { rejectWithValue }) => {
    try {
      const response = await materialService.getMaterials(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch materials');
    }
  }
);

export const fetchMaterialsList = createAsyncThunk(
  'material/fetchMaterialsList',
  async (params: MaterialsListParams, { rejectWithValue }) => {
    try {
      const response = await materialService.getMaterialsList(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch materials list');
    }
  }
);

export const fetchMaterialsByGst = createAsyncThunk(
  'material/fetchMaterialsByGst',
  async (params: MaterialsByGstParams, { rejectWithValue }) => {
    try {
      const response = await materialService.getMaterialsByGst(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch materials by GST');
    }
  }
);

export const createMaterial = createAsyncThunk(
  'material/createMaterial',
  async (data: MaterialFormData, { rejectWithValue }) => {
    try {
      const response = await materialService.createMaterial(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create material');
    }
  }
);

export const createBulkMaterials = createAsyncThunk(
  'material/createBulkMaterials',
  async (data: BulkMaterialFormData, { rejectWithValue }) => {
    try {
      const response = await materialService.createBulkMaterials(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create materials');
    }
  }
);

export const updateMaterial = createAsyncThunk(
  'material/updateMaterial',
  async ({ id, data }: { id: string; data: MaterialFormData }, { rejectWithValue }) => {
    try {
      const response = await materialService.updateMaterial(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update material');
    }
  }
);

export const deleteMaterial = createAsyncThunk(
  'material/deleteMaterial',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await materialService.deleteMaterial(id);
      return { ...response, id };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete material');
    }
  }
);

export const fetchMaterialById = createAsyncThunk(
  'material/fetchMaterialById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await materialService.getMaterialById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch material');
    }
  }
);

export const materialSlice = createSlice({
  name: 'material',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearMaterial: (state) => {
      state.material = null;
    },
    setMaterials: (state, action: PayloadAction<Material[]>) => {
      state.materials = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Materials
      .addCase(fetchMaterials.pending, (state) => {
        state.materialsLoading = true;
        state.error = null;
      })
      .addCase(fetchMaterials.fulfilled, (state, action) => {
        state.materialsLoading = false;
        state.materials = action.payload.data.docs;
        state.pagination = {
          currentPage: action.payload.data.page,
          totalPages: action.payload.data.totalPages,
          totalItems: action.payload.data.totalDocs,
          itemsPerPage: action.payload.data.limit,
        };
        state.error = null;
      })
      .addCase(fetchMaterials.rejected, (state, action) => {
        state.materialsLoading = false;
        state.error = action.payload as string;
      })

      // Fetch Materials List (filtered by company and supplier)
      .addCase(fetchMaterialsList.pending, (state) => {
        state.materialsListLoading = true;
        state.error = null;
      })
      .addCase(fetchMaterialsList.fulfilled, (state, action) => {
        state.materialsListLoading = false;
        state.materialsList = action.payload.data.docs;
        state.error = null;
      })
      .addCase(fetchMaterialsList.rejected, (state, action) => {
        state.materialsListLoading = false;
        state.error = action.payload as string;
      })

      // Fetch Materials by GST (for purchase order work process)
      .addCase(fetchMaterialsByGst.pending, (state) => {
        state.materialsLoading = true;
        state.error = null;
      })
      .addCase(fetchMaterialsByGst.fulfilled, (state, action) => {
        state.materialsLoading = false;
        state.materials = action.payload.data.docs;
        state.error = null;
      })
      .addCase(fetchMaterialsByGst.rejected, (state, action) => {
        state.materialsLoading = false;
        state.error = action.payload as string;
      })

      // Create Material
      .addCase(createMaterial.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createMaterial.fulfilled, (state, action) => {
        state.isLoading = false;
        state.materials.unshift(action.payload.data);
        state.error = null;
      })
      .addCase(createMaterial.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Create Bulk Materials
      .addCase(createBulkMaterials.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createBulkMaterials.fulfilled, (state,_) => {
        state.isLoading = false;
        // The API might return multiple materials or a single response
        // We'll refresh the list after bulk creation
        state.error = null;
      })
      .addCase(createBulkMaterials.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Update Material
      .addCase(updateMaterial.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateMaterial.fulfilled, (state, action) => {
        state.isLoading = false;
        const updatedMaterial = action.payload.data;
        const index = state.materials.findIndex(material => material._id === updatedMaterial._id);
        if (index !== -1) {
          state.materials[index] = updatedMaterial;
        }
        state.error = null;
      })
      .addCase(updateMaterial.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Delete Material
      .addCase(deleteMaterial.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteMaterial.fulfilled, (state, action) => {
        state.isLoading = false;
        state.materials = state.materials.filter(material => material._id !== action.payload.id);
        state.error = null;
      })
      .addCase(deleteMaterial.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch Material By ID
      .addCase(fetchMaterialById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMaterialById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.material = action.payload.data;
        state.error = null;
      })
      .addCase(fetchMaterialById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearMaterial, setMaterials } = materialSlice.actions;
export default materialSlice.reducer;